import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Empty, Modal, Popconfirm, Select, Space, Table, Tag, Tooltip, Typography, message } from 'antd';
import { ReloadOutlined, RedoOutlined, NodeIndexOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { adminApi } from '../../services/api';

// DNS 服务器组放置:产品绑定服务器组(NS 池),区域从组内健康的 NS 中分配;组内凑不出 2 个时
// 已付款订单挂起(不计费、不退款),这里可重试 / 手动分配 / 取消并退款。

const fmt = (s) => (s ? String(s).replace('T', ' ').slice(0, 16) : '-');

const NodeTags = ({ nodes }) => (
    <Space size={[4, 4]} wrap>
        {nodes.map((n) => (
            <Tooltip key={n.id} title={n.healthy ? '健康' : n.reason}>
                <Tag color={n.healthy ? 'green' : 'red'} style={{ marginInlineEnd: 0 }}>{n.hostname}</Tag>
            </Tooltip>
        ))}
        {!nodes.length && <Typography.Text type="secondary">无节点</Typography.Text>}
    </Space>
);

const DnsPlacement = () => {
    const [data, setData] = useState({ pools: [], held: [], degraded: [] });
    const [loading, setLoading] = useState(false);
    const [assign, setAssign] = useState(null); // {zone_id, zone, ids, delegated}
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await adminApi.get('/dns/placement');
            if (res.data.code === 0) setData(res.data.data);
            else message.error(res.data.message);
        } catch (e) {
            message.error('加载失败');
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const servableOptions = useMemo(() => data.pools.flatMap((p) => p.child_groups.filter((g) => g.servable).map((g) => ({
        value: g.id, label: `${g.ns_hostname}(${p.name},健康 ${g.healthy_nodes}/${g.total_nodes})`,
    }))), [data.pools]);

    const post = async (url, body, okText) => {
        try {
            const res = await adminApi.post(url, body);
            if (res.data.code === 0) {
                message.success(okText);
                load();
                return res.data.data;
            }
            message.error(res.data.message);
        } catch (e) {
            message.error('操作失败');
        }
        return null;
    };

    const saveAssign = async () => {
        setSaving(true);
        const r = await post('/dns/placement/assign', { zone_id: assign.zone_id, child_group_ids: assign.ids }, '已分配');
        setSaving(false);
        if (r) {
            if (r.activated === true) message.success('订单已开通');
            setAssign(null);
        }
    };

    const groupColumns = [
        { title: 'NS', dataIndex: 'ns_hostname', width: 240, render: (v, g) => <Space><Typography.Text code>{v || '-'}</Typography.Text>{g.status !== 'active' && <Tag>{g.status}</Tag>}</Space> },
        {
            title: '可分配', dataIndex: 'servable', width: 100,
            render: (v, g) => (v ? <Tag color="green">可用</Tag> : <Tooltip title={g.reason}><Tag color="red">不可用</Tag></Tooltip>),
        },
        { title: '健康节点', key: 'h', width: 100, render: (_, g) => `${g.healthy_nodes} / ${g.total_nodes}` },
        { title: '节点', dataIndex: 'nodes', render: (v) => <NodeTags nodes={v} /> },
        { title: '区域', dataIndex: 'zones', width: 80 },
    ];

    const heldColumns = [
        { title: '区域', dataIndex: 'zone', render: (v) => <Typography.Text code>{v || '-'}</Typography.Text> },
        { title: '订阅', dataIndex: 'subscription_id', width: 90, render: (v) => `#${v}` },
        { title: '用户', dataIndex: 'user_id', width: 80 },
        { title: '原因', dataIndex: 'reason', ellipsis: true },
        { title: '重试', dataIndex: 'retry_count', width: 70 },
        { title: '最近', dataIndex: 'last_error_at', width: 140, render: fmt },
        {
            title: '操作', key: 'op', width: 260, fixed: 'right',
            render: (_, r) => (
                <Space size={4}>
                    <Button size="small" icon={<RedoOutlined />} onClick={() => post('/dns/placement/retry', { subscription_id: r.subscription_id }, '已开通')}>重试</Button>
                    <Button size="small" icon={<NodeIndexOutlined />} disabled={!r.zone_id}
                        onClick={() => setAssign({ zone_id: r.zone_id, zone: r.zone, ids: [], delegated: false })}>分配</Button>
                    <Popconfirm title="取消订单并把实付金额退回余额?" onConfirm={() => post('/dns/placement/cancel', { subscription_id: r.subscription_id }, '已取消并退款')}>
                        <Button size="small" danger icon={<CloseCircleOutlined />}>取消退款</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const degradedColumns = [
        { title: '区域', dataIndex: 'zone', render: (v) => <Typography.Text code>{v}</Typography.Text> },
        { title: '当前 NS', dataIndex: 'child_group_ids', render: (ids, r) => ids.map((id) => <Tag key={id} color={r.unhealthy.includes(id) ? 'red' : 'default'}>#{id}</Tag>) },
        { title: '委派', dataIndex: 'ns_status', width: 90, render: (v) => (v === 'active' ? <Tag color="blue">已委派</Tag> : <Tag>{v || '未委派'}</Tag>) },
        {
            title: '操作', key: 'op', width: 100,
            render: (_, r) => <Button size="small" onClick={() => setAssign({ zone_id: r.zone_id, zone: r.zone, ids: r.child_group_ids.filter((i) => !r.unhealthy.includes(i)), delegated: r.ns_status === 'active' })}>重新分配</Button>,
        },
    ];

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Card
                title="DNS 服务器组分配"
                extra={<Button icon={<ReloadOutlined />} onClick={load} loading={loading}>刷新</Button>}
                bodyStyle={{ paddingTop: 12 }}
            >
                {data.held.length > 0 && (
                    <Alert type="error" showIcon style={{ marginBottom: 12 }} message={`${data.held.length} 个已付款订单因无可用 NS 挂起(未计费),系统每 5 分钟自动重试`} />
                )}
                {data.degraded.length > 0 && (
                    <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={`${data.degraded.length} 个区域有 NS 无健康节点(只剩另一个 NS 应答)`} />
                )}
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    {data.pools.filter((p) => p.bound || p.status === 'active').map((p) => (
                        <Card key={p.id} size="small" type="inner"
                            title={<Space wrap>{p.name}<Tag color={p.status === 'active' ? 'blue' : 'default'}>{p.status}</Tag>
                                <Tag color={p.servable >= 2 ? 'green' : 'red'}>可用 NS {p.servable}</Tag>
                                {p.products.map((x) => <Tag key={x.id}>{x.name}</Tag>)}</Space>}>
                            <Table rowKey="id" size="small" pagination={false} columns={groupColumns} dataSource={p.child_groups} scroll={{ x: 800 }}
                                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="无 NS" /> }} />
                        </Card>
                    ))}
                </Space>
            </Card>

            <Card title="挂起的订单" size="small">
                <Table rowKey="subscription_id" size="small" columns={heldColumns} dataSource={data.held} pagination={false} scroll={{ x: 900 }}
                    locale={{ emptyText: '无' }} />
            </Card>

            <Card title="降级的区域" size="small">
                <Table rowKey="zone_id" size="small" columns={degradedColumns} dataSource={data.degraded} pagination={{ pageSize: 20, hideOnSinglePage: true }} scroll={{ x: 600 }}
                    locale={{ emptyText: '无' }} />
            </Card>

            <Modal open={!!assign} title={`分配名称服务器:${assign?.zone || ''}`} onCancel={() => setAssign(null)} onOk={saveAssign}
                okButtonProps={{ disabled: (assign?.ids || []).length < 2, loading: saving }} okText="保存">
                {assign?.delegated && <Alert type="warning" showIcon style={{ marginBottom: 12 }} message="该区域已委派:更换 NS 后客户需到注册商改为新的 NS" />}
                <Select mode="multiple" style={{ width: '100%' }} placeholder="选择至少 2 个" value={assign?.ids || []}
                    onChange={(ids) => setAssign((a) => ({ ...a, ids }))} options={servableOptions} />
            </Modal>
        </Space>
    );
};

export default DnsPlacement;
